<template>
  <div class="mc-chart-card">
    <div class="mc-chart-card__head">
      <h3 class="mc-chart-card__title">
        {{ titulo }}
      </h3>
      <p class="mc-chart-card__sub">
        {{ subtitulo }}
      </p>
      <p
        v-if="insightLinha"
        class="mc-chart-card__insight"
      >
        {{ insightLinha }}
      </p>
    </div>

    <div class="mc-chart-card__legendas">
      <span class="mc-leg"><i class="mc-leg__dot mc-leg__dot--bot" /> Com bot</span>
      <span class="mc-leg"><i class="mc-leg__dot mc-leg__dot--sem" /> Sem bot</span>
      <span class="mc-leg mc-leg--soft"><i class="mc-leg__gap" /> Diferença = horas poupadas</span>
    </div>

    <div class="mc-chart-card__plot">
      <vue-apex-chart
        type="area"
        height="320"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'GraficoEconomiaBot',
  components: { 'vue-apex-chart': VueApexCharts },
  props: {
    titulo: {
      type: String,
      default: 'Impacto do bot no tempo de atendimento',
    },
    subtitulo: {
      type: String,
      default: '',
    },
    insightLinha: {
      type: String,
      default: '',
    },
    labels: {
      type: Array,
      default: () => [],
    },
    horasComBot: {
      type: Array,
      default: () => [],
    },
    horasSemBot: {
      type: Array,
      default: () => [],
    },
    anotacaoLabel: {
      type: String,
      default: '',
    },
  },
  computed: {
    chartSeries() {
      const a = this.horasComBot.length ? this.horasComBot : [0]
      const b = this.horasSemBot.length ? this.horasSemBot : [0]
      return [
        { name: 'Com bot', data: a },
        { name: 'Sem bot', data: b },
      ]
    },
    chartOptions() {
      const labels = this.labels.length ? this.labels : ['—']
      const { horasComBot } = this
      const { horasSemBot } = this
      const purple = '#7B3FE4'
      const lilac = '#C8B8E0'
      const lastIdx = Math.max(0, labels.length - 1)
      const yLast = horasComBot[lastIdx] != null ? horasComBot[lastIdx] : 0
      const annText = this.anotacaoLabel || 'Horas poupadas no período'

      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: "'Inter', 'Plus Jakarta Sans', system-ui, sans-serif",
          animations: { enabled: true, easing: 'easeinout', speed: 480 },
        },
        colors: [purple, lilac],
        stroke: { curve: 'smooth', width: [3, 2] },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.42,
            opacityTo: 0.06,
            stops: [0, 92, 100],
          },
        },
        dataLabels: { enabled: false },
        markers: {
          size: [5, 4],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: { sizeOffset: 2 },
          discrete: this.markersDiscrete,
        },
        legend: { show: false },
        xaxis: {
          categories: labels,
          labels: {
            style: { colors: '#7F6B9E', fontWeight: 600, fontSize: '11px' },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: '#7F6B9E', fontWeight: 600, fontSize: '11px' },
          },
          title: { text: 'Horas no período' },
        },
        grid: {
          borderColor: '#EADFF6',
          strokeDashArray: 4,
          padding: { left: 8, right: 16, top: 8 },
        },
        tooltip: {
          theme: 'light',
          shared: true,
          intersect: false,
          style: { fontSize: '13px' },
          custom: ({ dataPointIndex }) => {
            const i = dataPointIndex
            const cat = labels[i] || ''
            const com = horasComBot[i] != null ? horasComBot[i] : 0
            const sem = horasSemBot[i] != null ? horasSemBot[i] : 0
            const poup = Math.max(0, Math.round((sem - com) * 100) / 100)
            return `<div class="mc-apex-tip"><strong>${cat}</strong><br/>
              <span>Com bot: <strong>${com}</strong> h</span><br/>
              <span>Sem bot: <strong>${sem}</strong> h</span><br/>
              <span class="mc-apex-tip__diff">Diferença (horas poupadas): <strong>${poup}</strong> h</span></div>`
          },
        },
        annotations: {
          points: labels.length && annText
            ? [{
              x: labels[lastIdx],
              y: yLast,
              marker: { size: 0 },
              label: {
                borderColor: '#EADFF6',
                offsetY: -8,
                style: {
                  background: '#fff',
                  color: '#4B2A7B',
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: {
                    left: 10, right: 10, top: 6, bottom: 6,
                  },
                },
                text: annText,
              },
            }]
            : [],
        },
      }
    },
    markersDiscrete() {
      const n = this.horasComBot.length
      if (n === 0) return []
      const last = n - 1
      return [
        {
          seriesIndex: 0,
          dataPointIndex: last,
          fillColor: '#7B3FE4',
          strokeColor: '#fff',
          size: 7,
        },
        {
          seriesIndex: 1,
          dataPointIndex: last,
          fillColor: '#B5A3D4',
          strokeColor: '#fff',
          size: 6,
        },
      ]
    },
  },
}
</script>

<style scoped>
.mc-chart-card {
  background: var(--mc-surface);
  border-radius: var(--mc-radius-lg);
  border: 1px solid var(--mc-border);
  box-shadow: var(--mc-shadow-md);
  padding: 1.35rem 1.25rem 0.5rem;
  margin-bottom: 1.25rem;
  transition: transform 0.22s ease, box-shadow 0.22s ease;
}

.mc-chart-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px rgba(93, 42, 140, 0.1);
}

.mc-chart-card__head {
  margin-bottom: 0.5rem;
  padding: 0 0.15rem;
}

.mc-chart-card__title {
  font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
  font-size: 1.12rem;
  font-weight: 800;
  color: var(--mc-text);
  margin: 0 0 0.35rem;
  line-height: 1.25;
}

.mc-chart-card__sub {
  margin: 0;
  font-size: 0.84rem;
  font-weight: 500;
  color: var(--mc-text-muted);
  line-height: 1.45;
}

.mc-chart-card__insight {
  margin: 0.65rem 0 0;
  padding: 0.55rem 0.75rem;
  border-radius: var(--mc-radius-sm, 14px);
  background: linear-gradient(135deg, rgba(123, 63, 228, 0.08) 0%, rgba(228, 106, 169, 0.08) 100%);
  border: 1px solid rgba(234, 223, 246, 0.9);
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--mc-text);
  line-height: 1.4;
}

.mc-chart-card__legendas {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 1.1rem;
  margin-bottom: 0.35rem;
  padding: 0 0.15rem;
}

.mc-leg {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--mc-text-soft);
}

.mc-leg--soft {
  font-weight: 600;
  color: var(--mc-text-muted);
}

.mc-leg__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.mc-leg__dot--bot {
  background: linear-gradient(135deg, #7b3fe4, #a248d9);
}

.mc-leg__dot--sem {
  background: #c8b8e0;
}

.mc-leg__gap {
  width: 14px;
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(90deg, rgba(123, 63, 228, 0.35), rgba(200, 184, 224, 0.6));
  flex-shrink: 0;
}

.mc-chart-card__plot {
  margin: 0 -0.35rem;
}

.mc-chart-card >>> .mc-apex-tip {
  padding: 10px 14px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(93, 42, 140, 0.12);
  border: 1px solid var(--mc-border);
  color: var(--mc-text);
  font-size: 0.82rem;
  line-height: 1.45;
}

.mc-chart-card >>> .mc-apex-tip__diff {
  display: inline-block;
  margin-top: 0.25rem;
  color: var(--mc-purple);
  font-weight: 700;
}
</style>
